type AppThemeBase = {
    readonly colors: {
        readonly backdrop: string;
        readonly background: string;
        readonly elevation: {
            readonly level0: string;
            readonly level1: string;
            readonly level2: string;
            readonly level3: string;
            readonly level4: string;
            readonly level5: string;
        };
        readonly error: string;
        readonly errorContainer: string;
        readonly inverseOnSurface: string;
        readonly inversePrimary: string;
        readonly inverseSurface: string;
        readonly onBackground: string;
        readonly onError: string;
        readonly onErrorContainer: string;
        readonly onPrimary: string;
        readonly onPrimaryContainer: string;
        readonly onSecondary: string;
        readonly onSecondaryContainer: string;
        readonly onSurface: string;
        readonly onSurfaceDisabled: string;
        readonly onSurfaceVariant: string;
        readonly onTertiary: string;
        readonly onTertiaryContainer: string;
        readonly outline: string;
        readonly outlineVariant: string;
        readonly primary: string;
        readonly primaryContainer: string;
        readonly scrim: string;
        readonly secondary: string;
        readonly secondaryContainer: string;
        readonly shadow: string;
        readonly surface: string;
        readonly surfaceDisabled: string;
        readonly surfaceVariant: string;
        readonly tertiary: string;
        readonly tertiaryContainer: string;
    };
};

export type AppTheme = AppThemeBase & {
    readonly button: {
        readonly primary: string;
        readonly secondary: string;
    };
    readonly chart: {
        readonly match: string;
        readonly miss: string;
    };
    readonly entity: {
        readonly candidate: string;
        readonly certification: string;
        readonly course: string;
        readonly education: string;
        readonly jobPosition: string;
        readonly jobProfile: string;
        readonly project: string;
        readonly publication: string;
        readonly team: string;
    };
    readonly skill: {
        readonly hard: string;
        readonly language: string;
        readonly recommendation: string;
        readonly soft: string;
    };
    readonly tabBar: {
        readonly background: string;
    };
    readonly typography: {
        readonly bodyText: string;
        readonly primary: string;
        readonly secondary: string;
        readonly skillSearchChip: string;
    };
};

export const lightColorScheme: AppTheme = {
    button: {
        primary: '#262626',
        secondary: '#1F2937',
    },
    chart: {
        match: '#E80060',
        miss: '#f9acbb',
    },
    colors: {
        backdrop: 'rgba(48, 48, 56, 0.4)',
        background: 'rgb(243, 243, 243)',
        elevation: {
            level0: 'transparent',
            level1: 'rgb(245, 241, 255)',
            level2: 'rgb(239, 236, 255)',
            level3: 'rgb(233, 230, 255)',
            level4: 'rgb(232, 228, 255)',
            level5: 'rgb(228, 224, 255)',
        },
        error: 'rgb(204,68,75)',
        errorContainer: 'rgb(255, 218, 214)',
        inverseOnSurface: 'rgb(243, 239, 244)',
        inversePrimary: 'rgb(192, 193, 255)',
        inverseSurface: 'rgb(49, 48, 52)',
        onBackground: 'rgb(28, 27, 31)',
        onError: 'rgb(255,255,255)',
        onErrorContainer: 'rgb(65, 0, 2)',
        onPrimary: 'rgb(255, 255, 255)',
        onPrimaryContainer: 'rgb(5, 0, 109)',
        onSecondary: 'rgb(255, 255, 255)',
        onSecondaryContainer: 'rgb(0, 33, 16)',
        onSurface: 'rgb(38,38,38)',
        onSurfaceDisabled: 'rgb(173,173,173)',
        onSurfaceVariant: 'rgb(125,125,125)',
        onTertiary: 'rgb(255, 255, 255)',
        onTertiaryContainer: 'rgb(44, 0, 81)',
        outline: 'rgb(119, 118, 128)',
        outlineVariant: 'rgb(199, 197, 208)',
        primary: 'rgb(59, 59, 255)',
        primaryContainer: 'rgb(225, 224, 255)',
        scrim: 'rgb(0, 0, 0)',
        secondary: 'rgb(31,41,55) ',
        secondaryContainer: 'rgb(86, 255, 167)',
        shadow: 'rgb(0, 0, 0)',
        surface: 'rgb(255, 251, 255)',
        surfaceDisabled: 'rgba(28, 27, 31, 0.12)',
        surfaceVariant: 'rgb(228, 225, 236)',
        tertiary: 'rgb(191,196,205)',
        tertiaryContainer: 'rgb(240, 219, 255)',
    },
    entity: {
        candidate: '#0016DA',
        certification: '#FF9900',
        course: '#4200FF',
        education: '#0019FF',
        jobPosition: '#E80060',
        jobProfile: '#EA00D2',
        project: '#00B4DC',
        publication: '#00A67E',
        team: '#9400DA',
    },
    skill: {
        hard: '#007DFF',
        language: '#00BF90',
        recommendation: '#A1ABB5',
        soft: '#6400FF',
    },
    tabBar: {
        background: '#FFFFFF',
    },
    typography: {
        bodyText: '#595959',
        primary: '#262626',
        secondary: '#26262699',
        skillSearchChip: 'rgb(255, 251, 255)',
    },
};

// MD3 dark color scheme generated from: https://callstack.github.io/react-native-paper/docs/guides/theming/
// with #251DB0 as primary color input
// with #1F2937 as secondary color input
// with #795369 as tertiary color input
export const darkColorScheme: AppTheme = {
    button: {
        primary: '#E5E1E6',
        secondary: '#1F2937',
    },
    chart: {
        match: '#ffb2be',
        miss: '#9b4054',
    },
    colors: {
        backdrop: 'rgba(48, 48, 56, 0.4)',
        background: 'rgb(12, 12, 12)',
        elevation: {
            level0: 'transparent',
            level1: 'rgb(36, 35, 42)',
            level2: 'rgb(41, 40, 49)',
            level3: 'rgb(46, 45, 56)',
            level4: 'rgb(48, 47, 58)',
            level5: 'rgb(51, 50, 62)',
        },
        error: 'rgb(255, 180, 171)',
        errorContainer: 'rgb(147, 0, 10)',
        inverseOnSurface: 'rgb(49, 48, 52)',
        inversePrimary: 'rgb(76, 75, 211)',
        inverseSurface: 'rgb(229, 225, 230)',
        onBackground: 'rgb(229, 225, 230)',
        onError: 'rgb(105, 0, 5)',
        onErrorContainer: 'rgb(255, 180, 171)',
        onPrimary: 'rgb(22, 2, 167)',
        onPrimaryContainer: 'rgb(226, 223, 255)',
        onSecondary: 'rgb(0, 49, 92)',
        onSecondaryContainer: 'rgb(211, 228, 255)',
        onSurface: 'rgb(229, 225, 230)',
        onSurfaceDisabled: 'rgba(229, 225, 230, 0.38)',
        onSurfaceVariant: 'rgb(200, 197, 208)',
        onTertiary: 'rgb(70, 38, 58)',
        onTertiaryContainer: 'rgb(255, 216, 235)',
        outline: 'rgb(145, 143, 154)',
        outlineVariant: 'rgb(71, 70, 79)',
        primary: 'rgb(193, 193, 255)',
        primaryContainer: 'rgb(50, 46, 187)',
        scrim: 'rgb(0, 0, 0)',
        secondary: 'rgb(163, 201, 255)',
        secondaryContainer: 'rgb(0, 72, 130)',
        shadow: 'rgb(0, 0, 0)',
        surface: 'rgb(28, 27, 31)',
        surfaceDisabled: 'rgba(229, 225, 230, 0.12)',
        surfaceVariant: 'rgb(71, 70, 79)',
        tertiary: 'rgb(233, 185, 211)',
        tertiaryContainer: 'rgb(95, 60, 81)',
    },
    entity: {
        candidate: '#bdc2ff',
        certification: '#ffb86f',
        course: '#c5c0ff',
        education: '#bec2ff',
        jobPosition: '#ffb2be',
        jobProfile: '#fface8',
        project: '#4cd6ff',
        publication: '#5bdcb0',
        team: '#9400DA',
    },
    skill: {
        hard: '#acc7ff',
        language: '#45dead',
        recommendation: '#8bceff',
        soft: '#cdbdff',
    },
    tabBar: {
        background: 'rgb(28, 27, 31)',
    },
    typography: {
        bodyText: '#a6a6a6',
        primary: '#d9d9d9',
        secondary: '#7d7d7d',
        skillSearchChip: 'rgb(255, 251, 255)',
    },
};
